import { Elements, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCallback } from 'react';

import type { StripeError, StripeExpressCheckoutElementReadyEvent } from '@stripe/stripe-js';

import { useCart } from '../hooks/queries';
import { usePaymentMethodsAtom } from '../hooks/state/usePaymentMethodsAtom';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY!);
/**
 * Hidden component that detects available wallet payment methods early in the component tree.
 * Uses ExpressCheckoutElement's onReady callback for accurate detection.
 */
const WalletDetector = () => {
  const { changeWallets } = usePaymentMethodsAtom();

  const onReady = useCallback(
    ({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
      const wallets = [];
      if (availablePaymentMethods?.applePay) {
        wallets.push('applePay');
      }
      if (availablePaymentMethods?.googlePay) {
        wallets.push('googlePay');
      }

      changeWallets(wallets);
    },
    [changeWallets],
  );

  const onLoadError = useCallback((event: { elementType: 'expressCheckout'; error: StripeError }) => {
    console.error('[WalletDetector] load error', { event });
  }, []);

  // No-op handler - this element is only for detection, not actual payment processing
  const onConfirm = useCallback(() => {
    // This should never be called since the element is hidden
  }, []);

  return (
    <div style={{ display: 'none', position: 'absolute', pointerEvents: 'none' }} aria-hidden="true">
      <ExpressCheckoutElement
        options={{
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
        }}
        onReady={onReady}
        onConfirm={onConfirm}
        onLoadError={onLoadError}
      />
    </div>
  );
};

export const StripeProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: cart } = useCart();

  // Use cart currency and amount if available, otherwise use defaults
  const currency = cart?.currency?.toLowerCase() || 'eur';
  const amount = cart?.totalAmount ? Math.round(cart.totalAmount * 100) : 100;

  return (
    <>
      <Elements
        stripe={stripePromise}
        options={{
          mode: 'payment',
          currency,
          amount,
        }}
      >
        {cart && <WalletDetector />}
        {children}
      </Elements>
    </>
  );
};
